"use client";

import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Loader,
  toast,
  useDebounceCallback,
} from "@devhop/ui";
import { MapPin, PackagePlus, ScanLine } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { WarehouseLocationLookupResult } from "../api/client";
import {
  useAssignInventoryLocationMutation,
  useLookupSellableByBarcodeMutation,
  useLookupWarehouseLocationMutation,
  useSkuLocationBalancesQuery,
} from "../api/queries";

const SCAN_MAX_LEN = 64;

type Props = {
  shopId: string;
  /** ສາຂາທີ່ເລືອກໃນ UI — ຖ້າບໍ່ກົງກັບຊ່ອງທີ່ສະແກນ ຈະແຈ້ງເຕືອນ */
  branchId?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  canAdjust: boolean;
  initialScanCode?: string;
};

export function WarehouseLocationScanModal({
  shopId,
  branchId,
  open,
  onOpenChange,
  canAdjust,
  initialScanCode,
}: Props) {
  const lookupMut = useLookupWarehouseLocationMutation(shopId);
  const skuLookupMut = useLookupSellableByBarcodeMutation(shopId);
  const assignMut = useAssignInventoryLocationMutation(shopId);

  const locationInputRef = useRef<HTMLInputElement>(null);
  const skuInputRef = useRef<HTMLInputElement>(null);
  const scanLockRef = useRef(false);

  const [locationScan, setLocationScan] = useState("");
  const [lookupResult, setLookupResult] =
    useState<WarehouseLocationLookupResult | null>(null);
  const [lookupError, setLookupError] = useState<string | null>(null);

  const [assignOpen, setAssignOpen] = useState(false);
  const [skuScan, setSkuScan] = useState("");
  const [selectedSkuId, setSelectedSkuId] = useState("");
  const [selectedSkuLabel, setSelectedSkuLabel] = useState("");
  const [assignQty, setAssignQty] = useState(1);

  const effectiveBranchId = lookupResult?.location.branchId ?? branchId ?? "";
  const balancesQ = useSkuLocationBalancesQuery(
    shopId,
    assignOpen && effectiveBranchId && selectedSkuId ? effectiveBranchId : undefined,
    assignOpen && selectedSkuId ? selectedSkuId : undefined,
  );

  const numberFmt = useMemo(() => new Intl.NumberFormat("lo-LA"), []);

  const resetAssign = useCallback(() => {
    setAssignOpen(false);
    setSkuScan("");
    setSelectedSkuId("");
    setSelectedSkuLabel("");
    setAssignQty(1);
  }, []);

  const runLocationLookup = useCallback(
    async (raw: string) => {
      const code = raw.trim();
      if (!code) return;
      if (code.length > SCAN_MAX_LEN) {
        toast.error(`ລະຫັດຍາວເກີນໄປ (ສູງສຸດ ${SCAN_MAX_LEN} ຕົວ)`);
        return;
      }
      if (scanLockRef.current) return;
      scanLockRef.current = true;
      setLookupError(null);
      try {
        const res = await lookupMut.mutateAsync(code);
        if (
          branchId &&
          res.location.branchId !== branchId
        ) {
          toast.warning("ຊ່ອງນີ້ຢູ່ສາຂາອື່ນ — ກວດສອບສາຂາທີ່ເລືອກ");
        }
        setLookupResult(res);
        resetAssign();
        setLocationScan("");
        queueMicrotask(() => locationInputRef.current?.focus());
      } catch {
        setLookupResult(null);
        setLookupError("ບໍ່ພົບຕຳແໜ່ງຈາກ scanCode ນີ້");
      } finally {
        scanLockRef.current = false;
      }
    },
    [branchId, lookupMut, resetAssign],
  );

  const debouncedLocationScan = useDebounceCallback((value: string) => {
    const live = locationInputRef.current?.value ?? "";
    if (live !== value) return;
    void runLocationLookup(value);
  }, 90);

  const runSkuLookup = useCallback(
    async (raw: string) => {
      const code = raw.trim();
      if (!code || !lookupResult) return;
      try {
        const result = await skuLookupMut.mutateAsync(code);
        if (result.meta.total === 0) {
          toast.error("ບໍ່ພົບ SKU");
          return;
        }
        const row = result.data[0]!;
        const tm =
          row.trackingMode === "serial" ? "lot_serial" : row.trackingMode;
        if (tm !== "none") {
          toast.error("ຈັດຊ່ອງໄດ້ສະເພາະ SKU ໂໝດລວມ (none)");
          return;
        }
        setSelectedSkuId(row.id);
        setSelectedSkuLabel(`${row.productName} · ${row.shopSku}`);
        setSkuScan("");
        queueMicrotask(() => skuInputRef.current?.focus());
      } catch (e) {
        toast.error(e instanceof Error ? e.message : "ຄົ້ນຫາ SKU ລົ້ມເຫຼວ");
      }
    },
    [lookupResult, skuLookupMut],
  );

  const debouncedSkuScan = useDebounceCallback((value: string) => {
    const live = skuInputRef.current?.value ?? "";
    if (live !== value) return;
    void runSkuLookup(value);
  }, 90);

  async function handleAssign() {
    if (!lookupResult || !selectedSkuId || assignQty < 1) return;
    const maxUnlocated = balancesQ.data?.unlocatedQty ?? 0;
    if (assignQty > maxUnlocated) {
      toast.error(`ຍອດທີ່ຍັງບໍ່ຈັດຊ່ອງມີພຽງ ${maxUnlocated}`);
      return;
    }
    await assignMut.mutateAsync({
      branchId: lookupResult.location.branchId,
      shopProductSkuId: selectedSkuId,
      fromLocationId: null,
      toLocationId: lookupResult.location.id,
      qty: assignQty,
    });
    resetAssign();
    if (lookupResult.location.scanCode) {
      await runLocationLookup(lookupResult.location.scanCode);
    }
  }

  useEffect(() => {
    if (!open) {
      setLocationScan("");
      setLookupResult(null);
      setLookupError(null);
      resetAssign();
      return;
    }
    if (initialScanCode?.trim()) {
      void runLocationLookup(initialScanCode);
      return;
    }
    queueMicrotask(() => locationInputRef.current?.focus());
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset/focus only when dialog opens
  }, [open, initialScanCode]);

  const loc = lookupResult?.location;
  const maxUnlocated = balancesQ.data?.unlocatedQty ?? 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[min(92vh,720px)] flex-col gap-0 overflow-hidden p-0 sm:max-w-lg">
        <DialogHeader className="border-border border-b px-4 py-3">
          <DialogTitle className="flex items-center gap-2 text-base">
            <ScanLine className="size-5 text-primary" aria-hidden />
            ສະແກນຊ່ອງສາງ
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 space-y-4 overflow-y-auto px-4 py-4">
          <div className="space-y-1.5">
            <Label htmlFor="wh-loc-scan">scanCode / QR ຊ່ອງ</Label>
            <Input
              id="wh-loc-scan"
              ref={locationInputRef}
              value={locationScan}
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              placeholder="ສະແກນ QR ຊ່ອງ…"
              className="font-mono"
              onChange={(e) => {
                const v = e.target.value;
                setLocationScan(v);
                debouncedLocationScan(v);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  void runLocationLookup(locationScan);
                }
              }}
            />
            <p className="text-muted-foreground text-xs">
              ໃຊ້ສະແກນເຕີມືຖື ຫຼື ພິມ scanCode ແລ້ວ Enter
            </p>
          </div>

          {lookupMut.isPending ? (
            <div className="flex justify-center py-6">
              <Loader />
            </div>
          ) : lookupError ? (
            <p className="rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-destructive text-sm">
              {lookupError}
            </p>
          ) : loc ? (
            <>
              <div className="rounded-lg border border-border bg-muted/30 px-3 py-3">
                <p className="flex items-center gap-1.5 font-medium text-sm">
                  <MapPin className="size-4 text-primary" aria-hidden />
                  {loc.code}
                  {loc.name ? (
                    <span className="font-normal text-muted-foreground">
                      · {loc.name}
                    </span>
                  ) : null}
                </p>
                <p className="mt-0.5 text-muted-foreground text-xs">
                  ໂຊນ {loc.zoneCode} · {loc.zoneName}
                  {loc.isDefaultReceive ? " · ຮັບເຂົ້າເລີ່ມຕົ້ນ" : ""}
                </p>
              </div>

              <div>
                <p className="mb-2 font-medium text-sm">
                  ສິນຄ້າໃນຊ່ອງ ({lookupResult.items.length})
                </p>
                {lookupResult.items.length === 0 ? (
                  <p className="text-muted-foreground text-sm">ຊ່ອງວ່າງ</p>
                ) : (
                  <ul className="divide-y divide-border rounded-lg border border-border text-sm">
                    {lookupResult.items.map((item) => (
                      <li
                        key={item.shopProductSkuId}
                        className="flex items-start justify-between gap-2 px-3 py-2"
                      >
                        <div className="min-w-0">
                          <p className="truncate font-medium">
                            {item.productName}
                          </p>
                          <p className="font-mono text-muted-foreground text-xs">
                            {item.shopSku}
                          </p>
                        </div>
                        <span className="shrink-0 font-semibold tabular-nums">
                          {numberFmt.format(item.qtyOnHand)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {canAdjust && !assignOpen ? (
                <Button
                  type="button"
                  variant="secondary"
                  className="w-full gap-2"
                  onClick={() => {
                    setAssignOpen(true);
                    queueMicrotask(() => skuInputRef.current?.focus());
                  }}
                >
                  <PackagePlus className="size-4" aria-hidden />
                  ເພີ່ມເຂົ້າຊ່ອງ
                </Button>
              ) : null}

              {canAdjust && assignOpen ? (
                <div className="space-y-3 rounded-lg border border-primary/30 bg-primary/5 p-3">
                  <p className="font-medium text-sm">ເພີ່ມ qty ເຂົ້າຊ່ອງນີ້</p>
                  <div className="space-y-1.5">
                    <Label htmlFor="wh-sku-scan">ສະແກນ SKU / barcode</Label>
                    <Input
                      id="wh-sku-scan"
                      ref={skuInputRef}
                      value={skuScan}
                      autoComplete="off"
                      placeholder="ສະແກນສິນຄ້າ…"
                      className="font-mono"
                      onChange={(e) => {
                        const v = e.target.value;
                        setSkuScan(v);
                        debouncedSkuScan(v);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          void runSkuLookup(skuScan);
                        }
                      }}
                    />
                  </div>
                  {selectedSkuId ? (
                    <p className="text-sm">{selectedSkuLabel}</p>
                  ) : null}
                  {selectedSkuId && balancesQ.isLoading ? (
                    <Loader />
                  ) : selectedSkuId ? (
                    <p className="text-muted-foreground text-xs">
                      ຍັງບໍ່ຈັດຊ່ອງ:{" "}
                      <span className="font-medium text-foreground tabular-nums">
                        {numberFmt.format(maxUnlocated)}
                      </span>
                    </p>
                  ) : null}
                  <div className="space-y-1.5">
                    <Label htmlFor="wh-assign-qty">ຈຳນວນ</Label>
                    <Input
                      id="wh-assign-qty"
                      type="number"
                      min={1}
                      max={Math.max(1, maxUnlocated)}
                      value={assignQty}
                      disabled={!selectedSkuId}
                      onChange={(e) =>
                        setAssignQty(Math.max(1, Number(e.target.value) || 1))
                      }
                    />
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => void handleAssign()}
                      disabled={
                        assignMut.isPending ||
                        !selectedSkuId ||
                        maxUnlocated < 1 ||
                        assignQty < 1
                      }
                    >
                      ຢືນຢັນ
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={resetAssign}
                    >
                      ຍົກເລີກ
                    </Button>
                  </div>
                </div>
              ) : null}
            </>
          ) : null}
        </div>

        <DialogFooter className="border-border border-t px-4 py-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            ປິດ
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
